import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useRef, useState } from 'react';
import { Badge, BottomSheetContentLayout, Menu, Popover, Typography, useDevice, useOverlayContext } from 'seagull';
import { ExclamationMark, PlusCircle } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useLocaleKeys } from '~/i18n';
import { TriageCollapseWrapper } from '../TriageCollapseWrapper/TriageCollapseWrapper';
import { TriageFieldBox } from '../TriageFieldBox/TriageFieldBox';
import { TriageRow } from '../TriageRow/TriageRow';
import { TriageContactForm } from './TriageContactForm';
import s from './TriageContactRow.module.scss';
const formatContactSummary = (param)=>{
    let { companyName, firstName, lastName, email } = param;
    const name = [
        firstName,
        lastName
    ].filter((part)=>part != null && part !== '').join(' ');
    const hasCompany = companyName != null && companyName !== '';
    const hasName = name !== '';
    const hasEmail = email != null && email !== '';
    if (hasCompany && hasName && hasEmail) return "".concat(companyName, ", ").concat(name, " (").concat(email, ")");
    if (hasCompany && hasName) return "".concat(companyName, ", ").concat(name);
    if (hasCompany) return companyName;
    if (hasName && hasEmail) return "".concat(name, " (").concat(email, ")");
    if (hasName) return name;
    if (hasEmail) return email;
    return '';
};
export const GetTriageContactRowTID = (parentTestId)=>getTestIds(parentTestId);
export const PrimaryContactRowTestIds = GetTriageContactRowTID().PrimaryContactRowTestIds;
const MissingFieldsWarning = ()=>{
    const { t } = useLocaleKeys('agreementImport.triageModal');
    return /*#__PURE__*/ _jsxs("div", {
        className: s.missingFieldsWarning,
        "data-testid": PrimaryContactRowTestIds.MissingFieldsWarning(),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                children: /*#__PURE__*/ _jsx(ExclamationMark, {
                    size: 16
                })
            }),
            /*#__PURE__*/ _jsx(Typography, {
                children: t.primaryContactMissingFieldsWarning()
            })
        ]
    });
};
const ContactPickerMenu = (param)=>{
    let { contacts, selectedContactId, onSelectExisting, onSelectCreateNew } = param;
    const { t } = useLocaleKeys('agreementImport.triageModal');
    return /*#__PURE__*/ _jsx(Menu, {
        testId: PrimaryContactRowTestIds.ContactPicker.Root(),
        searchProps: {
            showSearch: true,
            autoFocus: true,
            strategy: {
                type: 'custom',
                filterItem: (search, item)=>{
                    var _item_title, _item_body;
                    const haystack = "".concat((_item_title = item.title) !== null && _item_title !== void 0 ? _item_title : '', " ").concat((_item_body = item.body) !== null && _item_body !== void 0 ? _item_body : '').toLowerCase();
                    return haystack.includes(search.trim().toLowerCase());
                }
            }
        },
        items: contacts.map((c)=>({
                id: c.id(),
                testId: PrimaryContactRowTestIds.ContactPicker.Option(c.id()),
                title: c.companyName(),
                body: "".concat(c.fullName(), " (").concat(c.email(), ")"),
                selected: c.id() === selectedContactId,
                onClick: ()=>onSelectExisting(c)
            })),
        footer: {
            items: [
                {
                    variant: 'contextualAction',
                    props: {
                        title: t.primaryContactContactsMenuCreateNew(),
                        icon: /*#__PURE__*/ _jsx(PlusCircle, {}),
                        testId: PrimaryContactRowTestIds.ContactPicker.CreateNew(),
                        onClick: onSelectCreateNew
                    }
                }
            ]
        }
    });
};
const ContactPickerSheet = (param)=>{
    let { contacts, selectedContactId, onSelectExisting, onSelectCreateNew } = param;
    const { hideBottomSheet } = useOverlayContext();
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        children: /*#__PURE__*/ _jsx(ContactPickerMenu, {
            contacts: contacts,
            selectedContactId: selectedContactId,
            onSelectExisting: (c)=>{
                onSelectExisting(c);
                hideBottomSheet();
            },
            onSelectCreateNew: onSelectCreateNew
        })
    });
};
const ContactFormSheet = (param)=>{
    let { client, hasAllRequired, onFieldChange, onChangeRequest } = param;
    const { t } = useLocaleKeys('agreementImport.triageModal');
    return /*#__PURE__*/ _jsxs(BottomSheetContentLayout, {
        contentClassName: s.sheetContent,
        footer: {
            secondaryAction: {
                text: t.primaryContactChangeButton(),
                testId: PrimaryContactRowTestIds.MobileChangeContactButton(),
                onClick: onChangeRequest
            }
        },
        children: [
            !hasAllRequired && /*#__PURE__*/ _jsx(MissingFieldsWarning, {}),
            /*#__PURE__*/ _jsx(TriageContactForm, {
                contact: client,
                onFieldChange: onFieldChange
            }, client.matchStatus === 'matched' ? client.matchedContactId : 'unmatched')
        ]
    });
};
export const TriageContactRow = (param)=>{
    let { model, onModelChange } = param;
    const { t } = useLocaleKeys('agreementImport.triageModal');
    const { isMobileDevice } = useDevice();
    const { pushBottomSheetPage, popBottomSheetPage } = useOverlayContext();
    const isUnmatched = model.isContactUnmatched();
    const hasAllRequired = model.contactHasAllRequiredFields();
    const [panelOpen, setPanelOpen] = useState(isUnmatched && !hasAllRequired);
    const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
    const pickerOpen = pickerAnchorEl != null;
    const { contacts } = useContacts();
    const client = model.client();
    const selectedContactId = client.matchStatus === 'matched' ? client.matchedContactId : undefined;
    const modelRef = useRef(model);
    modelRef.current = model;
    const handleFieldChange = useCallback((field, value)=>{
        onModelChange(modelRef.current.setContactField(field, value));
    }, [
        onModelChange
    ]);
    const commitSelectExisting = (contact)=>{
        const phone = contact.phone();
        onModelChange(modelRef.current.matchContactToExisting(contact.id(), {
            companyName: contact.companyName(),
            firstName: contact.firstName(),
            lastName: contact.lastName(),
            email: contact.email(),
            phone: phone == null ? undefined : phone
        }));
    };
    const pushFormSheet = (sourceModel, onChangeRequest)=>{
        pushBottomSheetPage({
            content: /*#__PURE__*/ _jsx(ContactFormSheet, {
                client: sourceModel.client(),
                hasAllRequired: sourceModel.contactHasAllRequiredFields(),
                onFieldChange: handleFieldChange,
                onChangeRequest: onChangeRequest
            })
        });
    };
    const pushPickerSheet = (onSelectCreateNew)=>{
        pushBottomSheetPage({
            fullHeight: true,
            content: /*#__PURE__*/ _jsx(ContactPickerSheet, {
                contacts: contacts,
                selectedContactId: selectedContactId,
                onSelectExisting: commitSelectExisting,
                onSelectCreateNew: onSelectCreateNew
            })
        });
    };
    const openFormSheetFlow = (sourceModel)=>{
        pushFormSheet(sourceModel, ()=>pushPickerSheet(popBottomSheetPage));
    };
    const openSheet = ()=>{
        if (isUnmatched) {
            openFormSheetFlow(modelRef.current);
        } else {
            pushPickerSheet(()=>{
                const unmatched = modelRef.current.unmatchContact();
                onModelChange(unmatched);
                popBottomSheetPage();
                openFormSheetFlow(unmatched);
            });
        }
    };
    const mobileBody = /*#__PURE__*/ _jsxs("div", {
        className: s.mobileSummaryBody,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: s.mobileSummaryLine,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: s.mobileSummaryText,
                        children: formatContactSummary(client)
                    }),
                    isUnmatched && /*#__PURE__*/ _jsx("span", {
                        "data-testid": PrimaryContactRowTestIds.NewContactBadge(),
                        className: s.mobileSummaryBadge,
                        children: /*#__PURE__*/ _jsx(Badge, {
                            skin: "primary",
                            rounded: true,
                            outlined: true,
                            children: t.primaryContactNewContactBadge()
                        })
                    })
                ]
            }),
            isUnmatched && !hasAllRequired && /*#__PURE__*/ _jsx(MissingFieldsWarning, {})
        ]
    });
    const handleSelectExisting = (contact)=>{
        commitSelectExisting(contact);
        setPanelOpen(false);
        setPickerAnchorEl(null);
    };
    const handleCreateNew = ()=>{
        onModelChange(modelRef.current.unmatchContact());
        setPanelOpen(true);
        setPickerAnchorEl(null);
    };
    const actions = [
        ...isUnmatched ? [
            {
                text: t.primaryContactEditButton(),
                testId: PrimaryContactRowTestIds.EditButton(),
                onClick: ()=>setPanelOpen((o)=>!o)
            }
        ] : [],
        {
            text: t.primaryContactChangeButton(),
            testId: PrimaryContactRowTestIds.ChangeButton(),
            onClick: (event)=>setPickerAnchorEl(event.currentTarget)
        }
    ];
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": PrimaryContactRowTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(TriageRow, {
                label: t.primaryContactLabel(),
                mobile: {
                    body: mobileBody,
                    bodyTestId: PrimaryContactRowTestIds.MobileSummaryBody(),
                    onEdit: openSheet,
                    editTestId: PrimaryContactRowTestIds.MobileEditButton()
                },
                children: /*#__PURE__*/ _jsx(TriageFieldBox, {
                    variant: {
                        type: 'text',
                        value: formatContactSummary(client),
                        testId: PrimaryContactRowTestIds.Summary()
                    },
                    forceActionsVisible: pickerOpen,
                    badges: isUnmatched && /*#__PURE__*/ _jsx("span", {
                        "data-testid": PrimaryContactRowTestIds.NewContactBadge(),
                        children: /*#__PURE__*/ _jsx(Badge, {
                            skin: "primary",
                            rounded: true,
                            outlined: true,
                            children: t.primaryContactNewContactBadge()
                        })
                    }),
                    actions: actions
                })
            }),
            !isMobileDevice && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Popover, {
                        anchorEl: pickerAnchorEl,
                        open: pickerOpen,
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'right'
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'right'
                        },
                        onClose: ()=>setPickerAnchorEl(null),
                        children: /*#__PURE__*/ _jsx("div", {
                            "data-testid": PrimaryContactRowTestIds.ContactPicker.Root(),
                            children: /*#__PURE__*/ _jsx(ContactPickerMenu, {
                                contacts: contacts,
                                selectedContactId: selectedContactId,
                                onSelectExisting: handleSelectExisting,
                                onSelectCreateNew: handleCreateNew
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsxs(TriageCollapseWrapper, {
                        open: panelOpen,
                        testId: PrimaryContactRowTestIds.Panel(),
                        children: [
                            !hasAllRequired && /*#__PURE__*/ _jsx(MissingFieldsWarning, {}),
                            /*#__PURE__*/ _jsx(TriageContactForm, {
                                contact: client,
                                onFieldChange: handleFieldChange
                            }, client.matchStatus === 'matched' ? client.matchedContactId : 'unmatched')
                        ]
                    })
                ]
            })
        ]
    });
};
